import { supabase } from './supabaseClient';

async function getTeacherId() {
  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (!authUser) return null;
  const { data: teacher } = await supabase
    .from('teachers')
    .select('id')
    .eq('auth_user_id', authUser.id)
    .maybeSingle();
  return teacher?.id || null;
}

// chat_messages.student_id stores the student's SLUG (invite_slug — same
// value the app passes as studentId={studentData.slug} / from useParams()),
// NOT students.id. Every other unread count (homework, chat_read_state) is
// keyed by students.id (uuid) to match what Sidebar/Students/StudentProfile
// already index into. Fetch both per student so chat can be translated
// slug -> uuid before being merged with the rest.
async function getTeacherStudents(teacherId) {
  const { data, error } = await supabase
    .from('students')
    .select('id, invite_slug')
    .eq('teacher_id', teacherId);
  if (error) {
    console.error('getTeacherStudents error:', error.message);
    return [];
  }
  return data || [];
}

/** Per-student count of submitted-but-not-yet-reviewed homework, keyed by student_id. */
export async function getUnreadHomeworkByStudent() {
  const teacherId = await getTeacherId();
  if (!teacherId) return {};
  const { data, error } = await supabase
    .from('homework')
    .select('student_id, students!inner(teacher_id)')
    .eq('status', 'submitted')
    .eq('students.teacher_id', teacherId);
  if (error) {
    console.error('getUnreadHomeworkByStudent error:', error.message);
    return {};
  }
  const byStudent = {};
  (data || []).forEach(row => {
    byStudent[row.student_id] = (byStudent[row.student_id] || 0) + 1;
  });
  return byStudent;
}

/** Mark a student's chat as read by the teacher (upserts the read cursor). */
export async function markChatRead(studentId) {
  if (!studentId) return;
  const { error } = await supabase
    .from('chat_read_state')
    .upsert({ student_id: studentId, teacher_last_read_at: new Date().toISOString() });
  if (error) {
    console.error('markChatRead error:', error.message);
    return;
  }
  // Nothing else listens for "a chat got marked read" otherwise — the
  // sidebar/students-list badges are fetched once by useTeacherUnreadCounts
  // and only refresh on 'homeworkUpdated'/'chatMessageSent'/tab-visibility,
  // none of which fire just from opening a chat. Without this, the badge
  // stayed stuck until something unrelated happened to trigger a refresh.
  try {
    window.dispatchEvent(new CustomEvent('chatRead', { detail: { studentId } }));
  } catch (_) {}
}

/**
 * Per-student count of unread messages SENT BY the student (i.e. the
 * teacher hasn't opened that student's chat since), keyed by student_id.
 * A student with no chat_read_state row yet has never been "read", so
 * every one of their messages counts.
 */
export async function getUnreadChatByStudent() {
  const teacherId = await getTeacherId();
  if (!teacherId) return {};

  const students = await getTeacherStudents(teacherId);
  if (students.length === 0) return {};
  const studentUuids = students.map(s => String(s.id));
  const slugToUuid = {};
  students.forEach(s => { if (s.invite_slug) slugToUuid[s.invite_slug] = String(s.id); });
  const slugs = Object.keys(slugToUuid);
  if (slugs.length === 0) return {};

  // chat_read_state is keyed by students.id (uuid) — see SUPABASE_CHAT_READ_STATE_SETUP.sql.
  const { data: readState, error: readError } = await supabase
    .from('chat_read_state')
    .select('student_id, teacher_last_read_at')
    .in('student_id', studentUuids);
  if (readError) console.error('getUnreadChatByStudent (read state) error:', readError.message);
  const lastReadByUuid = {};
  (readState || []).forEach(row => { lastReadByUuid[row.student_id] = row.teacher_last_read_at; });

  // chat_messages.student_id is the student's slug — see the note on
  // getTeacherStudents above.
  const { data: messages, error: msgError } = await supabase
    .from('chat_messages')
    .select('student_id, created_at')
    .eq('sender_role', 'student')
    .in('student_id', slugs);
  if (msgError) {
    console.error('getUnreadChatByStudent (messages) error:', msgError.message);
    return {};
  }

  const byStudent = {};
  (messages || []).forEach(row => {
    const uuid = slugToUuid[row.student_id];
    if (!uuid) return;
    const lastRead = lastReadByUuid[uuid] || '1970-01-01T00:00:00.000Z';
    if (row.created_at > lastRead) {
      byStudent[uuid] = (byStudent[uuid] || 0) + 1;
    }
  });
  return byStudent;
}

/** Combined per-student unread counts: { [studentId]: { homework, chat, total } }. */
export async function getUnreadCountsByStudent() {
  const [homeworkByStudent, chatByStudent] = await Promise.all([
    getUnreadHomeworkByStudent(),
    getUnreadChatByStudent(),
  ]);
  const studentIds = new Set([...Object.keys(homeworkByStudent), ...Object.keys(chatByStudent)]);
  const result = {};
  studentIds.forEach(id => {
    const homework = homeworkByStudent[id] || 0;
    const chat = chatByStudent[id] || 0;
    result[id] = { homework, chat, total: homework + chat };
  });
  return result;
}
